<template>
  <div
    ref="control"
    :class="[$style.Control__pane]"
    :style="controlStyle()"
    @mousedown.self.stop="emitClick"
    @touchstart.self.stop="emitClick"
    @contextmenu.prevent
  >
    <base-icon
      v-if="active"
      name="close-icon"
      size="xs"
      :class="$style.Control__remove"
      class="border"
      title-translation="icons.delete-element"
      @click.native="removeItem"
    />
    <base-icon
      v-if="active"
      name="resize-icon"
      size="xs"
      :class="$style.Control__resize"
      class="border"
    />
    <base-icon
      v-if="active && 1 === elements.length"
      name="edit-icon"
      size="xs"
      :class="$style.Control__edit"
      class="border"
      @click.native="editItem"
    />
  </div>
</template>

<script>
import { TrackingMixin } from '@/mixins/gtm-tracking';
import interact from 'interactjs';
import { mapState } from 'pinia';
import { useControlLayerStore } from '@/store-pinia/control-layer';
import { useClipboardStore } from '@/store-pinia/clipboard';
import ConfirmationModal from '@/components/modal/ConfirmationModal.vue';

export default {
  name: 'EditorItemControls',
  mixins: [TrackingMixin],
  props: {
    elements: {
      type: Array,
      required: true
    },
    active: {
      type: Boolean,
      required: true,
      default: () => false
    },
    ratio: {
      type: Number,
      required: true
    }
  },
  data: () => ({
    interactStart: {
      initialWidthInPixel: null,
      initialHeightInPixel: null,
      angle: null,
      boundaries: {},
      elements: {}
    },
    fingerAngleDelta: 0,
    dimensions: {}
  }),
  computed: {
    ...mapState(useControlLayerStore, ['config'])
  },
  watch: {
    elements: {
      handler: 'calculateElementDimensions',
      deep: true,
      immediate: true
    },
    active(isActive) {
      if (isActive) {
        window.addEventListener('keydown', this.keyListener);
      } else {
        window.removeEventListener('keydown', this.keyListener);
      }
    }
  },
  mounted() {
    interact(this.$refs.control)
      .draggable({
        onstart: this.recordElementsAtInteractionStart,
        onmove: this.dragMoveListener
      })
      .gesturable({
        onmove: event => {
          this.fingerAngleDelta += event.da;

          // between -360 and +360
          let rotationChange = this.fingerAngleDelta;
          // between 0 and +360
          if (rotationChange < 0) {
            rotationChange += 360;
          }
          // between 0 and -360
          if (rotationChange <= 5 || rotationChange >= 360 - 5) {
            rotationChange = 0;
          }

          this.$emit(
            'update',
            this.elements.map(element => ({
              uid: element.uid,
              changeset: {
                rotation:
                  this.interactStart.elements[element.uid].rotation +
                  rotationChange
              }
            }))
          );

          this.emitResize(event.scale);
        },
        onstart: this.recordElementsAtInteractionStart
      })
      .resizable({
        edges: {
          top: false,
          right: `.${this.$style.Control__resize}`,
          bottom: `.${this.$style.Control__resize}`,
          left: false
        },
        preserveAspectRatio: true,
        onstart: this.recordElementsAtInteractionStart,
        onmove: this.resizeMoveListener
      })
      .on(['gesture'], () => {
        this.trackEvent({
          action: 'GestureElement'
        });
      })
      .on(['dragend'], () => {
        this.trackEvent({
          action: 'MoveElement'
        });
        this.snapBackIfOutside();
        this.calculateElementDimensions();
        if (this.isBelowVisibilityTreshold()) {
          this.showRemovalModal().then(
            () => {
              this.removeItem();
            },
            () => {}
          );
        }
      })
      .on(['resizeend'], () => {
        this.trackEvent({
          action: 'ResizeElement'
        });
        this.snapBackIfOutside();
      });
    if (1 === this.elements.length) {
      const store = useControlLayerStore();
      this.$watch('elements.0', function (newValue) {
        store.getWatchers(newValue.uid).map(watcher => watcher(newValue));
      });
    }
    if (this.active) {
      window.addEventListener('keydown', this.keyListener);
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.keyListener);
  },
  methods: {
    keyListener(ev) {
      // We only want to listen to keybindings if there is no focus on other elements.
      // This seems to be the case, as long as the activeElement is equal to the body.
      if (document.activeElement !== document.body) {
        return;
      }

      const accelerator = ev.shiftKey ? 4 : 0.5;
      const key = ev.key.toLowerCase();
      const isCtrlOrMeta = ev.ctrlKey || ev.metaKey;

      if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(key)) {
        ev.preventDefault();
        const direction = {
          arrowup: { y: -accelerator },
          arrowdown: { y: accelerator },
          arrowleft: { x: -accelerator },
          arrowright: { x: accelerator }
        };
        this.moveElements(direction[key]);
      } else if (key === 'escape') {
        ev.preventDefault();
        this.$emit('unselect');
      } else if (['backspace', 'delete'].includes(key)) {
        ev.preventDefault();
        this.removeItem();
      } else if (key === 'c' && isCtrlOrMeta) {
        ev.preventDefault();
        useClipboardStore().copy(this.elements);
      } else if (key === 'd' && isCtrlOrMeta) {
        ev.preventDefault();
        this.elements.forEach(element =>
          this.$emit('add', {
            ...element,
            left: element.left + 4,
            top: element.top + 4
          })
        );
      }
    },
    emitClick() {
      if (this.elements.length > 1) {
        return;
      }
      this.elements.forEach(element => this.$emit('click', element.uid));
    },
    controlStyle() {
      if (this.elements.length === 0) {
        return { display: 'none' };
      }

      if (!this.active && this.elements.length === 1) {
        const element = this.elements[0];
        return {
          zIndex: this.config.controlLayerZIndex + element.layerLevel,
          top: `${element.top}%`,
          left: `${element.left}%`,
          height: `${element.height}%`,
          width: `${element.width}%`,
          transform: `rotate(${element.rotation || 0}deg)`
        };
      }

      const dimensions = this.dimensions.all;
      if (!dimensions) return {};

      const outlineColor = this.isBelowVisibilityTreshold() ? 'red' : '#4e5461';

      return {
        zIndex: dimensions.layerLevel,
        top: `${dimensions.top}%`,
        left: `${dimensions.left}%`,
        height: `${dimensions.height}%`,
        width: `${dimensions.width}%`,
        outline: this.active ? `solid 2px ${outlineColor}` : null
      };
    },
    calculateElementDimensions() {
      if (this.elements.length === 0) return;

      const dimensions = this.elements.reduce((prev, element) => {
        const { top, left, width, height } =
          this.calculateBoundingRect(element);

        this.$set(this.dimensions, element.uid, { top, left, width, height });

        if (!prev) {
          return {
            top,
            left,
            width,
            height,
            layerLevel: this.config.controlLayerZIndex + element.layerLevel
          };
        }

        return {
          top: Math.min(prev.top, top),
          left: Math.min(prev.left, left),
          width:
            Math.max(prev.left + prev.width, left + width) -
            Math.min(prev.left, left),
          height:
            Math.max(prev.top + prev.height, top + height) -
            Math.min(prev.top, top),
          layerLevel: this.config.controlLayerZIndex + element.layerLevel
        };
      }, null);

      this.$set(this.dimensions, 'all', dimensions);
    },
    removeItem() {
      this.elements.forEach(element => this.$emit('delete', element.uid));
    },
    editItem() {
      if (1 !== this.elements.length) {
        return;
      }
      this.elements.forEach(element => this.$emit('edit', element.uid));
    },
    recordElementsAtInteractionStart() {
      this.fingerAngleDelta = 0;
      this.interactStart.initialWidthInPixel = this.$refs.control.clientWidth;
      this.interactStart.initialHeightInPixel = this.$refs.control.clientHeight;
      this.interactStart.angle =
        (Math.atan(
          this.interactStart.initialHeightInPixel /
            this.interactStart.initialWidthInPixel
        ) *
          180) /
        Math.PI;
      this.interactStart.boundaries = JSON.parse(
        JSON.stringify(this.dimensions)
      );

      this.interactStart.elements = this.elements.reduce(
        (prev, element) => ({ ...prev, [element.uid]: element }),
        {}
      );
    },
    snapBackIfOutside() {
      if (
        this.elements.filter(element => {
          const dimension = this.calculateBoundingRect(element);
          return (
            dimension.width + dimension.left < 0 ||
            dimension.left > 100 ||
            dimension.height + dimension.top < 0 ||
            dimension.top > 100
          );
        }).length === 0
      ) {
        return;
      }

      this.$emit(
        'update',
        this.elements.map(element => ({
          uid: element.uid,
          changeset: {
            left: this.interactStart.elements[element.uid].left,
            top: this.interactStart.elements[element.uid].top,
            width: this.interactStart.elements[element.uid].width,
            height: this.interactStart.elements[element.uid].height
          }
        }))
      );
    },
    dragMoveListener(event) {
      this.$emit(
        'update',
        this.elements.map(element => ({
          uid: element.uid,
          changeset: {
            left:
              this.interactStart.elements[element.uid].left +
              ((event.client.x - event.clientX0) /
                event.target.parentElement.clientWidth) *
                100,
            top:
              this.interactStart.elements[element.uid].top +
              ((event.client.y - event.clientY0) /
                event.target.parentElement.clientHeight) *
                100
          }
        }))
      );
    },
    calculateBoundingRect(element) {
      const { top, left, height, width } = element;

      if (0 === element.rotation) {
        return { top, left, height, width };
      }

      // Transform height percentage value to width units, so we can calculate more easily
      const heightInWidthUnits = height / this.ratio;

      // Pythagoras
      const lengthDiagonale = Math.sqrt(width ** 2 + heightInWidthUnits ** 2);

      // Calculating the angle of the diagonale of the element's rectangle
      const angleDiagonale =
        (Math.atan(heightInWidthUnits / width) * 180) / Math.PI;

      // Calculate all 4 angles from the center towards the corners of the element's rectangle
      const angles = [
        angleDiagonale,
        360 - angleDiagonale,
        180 + angleDiagonale,
        180 - angleDiagonale
      ];

      // Calculate width and height of each corner
      // - first calculate the x coordinates on a circle where the center is 0/0, formula: X = r⋅cos(φ)
      // - radius is actually lengthDiagonale / 2, but to get the actual height we need to multiply by 2 again
      //   so this gets shortened out.
      // - extract the biggest width
      // This applies accordingly for y coordinate and height. Formula: Y=r⋅sin(φ)
      const calculatedHeightInWidthUnits = angles
        .map(
          angle =>
            lengthDiagonale *
            Math.sin(((angle + element.rotation) / 180) * Math.PI)
        )
        .reduce(
          (maxHeight, potentialHeight) => Math.max(maxHeight, potentialHeight),
          0
        );

      const calculatedWidth = angles
        .map(
          angle =>
            lengthDiagonale *
            Math.cos(((angle + element.rotation) / 180) * Math.PI)
        )
        .reduce(
          (maxWidth, potentialWidth) => Math.max(maxWidth, potentialWidth),
          0
        );

      // top inset need to be adjusted by half of the height difference
      // this applies to left inset and width as well
      return {
        top:
          top -
          ((calculatedHeightInWidthUnits - heightInWidthUnits) / 2) *
            this.ratio,
        left: left - (calculatedWidth - width) / 2,
        height: calculatedHeightInWidthUnits * this.ratio,
        width: calculatedWidth
      };
    },
    resizeMoveListener(event) {
      // Vector of the resize drag
      const vectorY = event.page.y - event.y0;
      const vectorX = event.page.x - event.x0;

      let vectorAngle = (Math.atan(vectorY / vectorX) * 180) / Math.PI;
      vectorAngle = vectorAngle < 0 ? 180 + vectorAngle : vectorAngle;
      vectorAngle = vectorY < 0 ? 180 + vectorAngle : vectorAngle;

      const deltaVectorBaseAngle = Math.min(
        Math.abs(vectorAngle - this.interactStart.angle),
        360 - Math.abs(vectorAngle - this.interactStart.angle)
      );
      const direction = deltaVectorBaseAngle > 90 ? -1 : 1;
      const vectorBaseLineAngle =
        90 - Math.min(180 - deltaVectorBaseAngle, deltaVectorBaseAngle);

      const resizeLength =
        Math.sqrt(vectorY ** 2 + vectorX ** 2) *
        Math.sin((vectorBaseLineAngle / 180) * Math.PI);

      const resizeRatio =
        resizeLength /
        Math.sqrt(
          this.interactStart.initialWidthInPixel ** 2 +
            this.interactStart.initialHeightInPixel ** 2
        );
      const sizeChangeFactor = 1 + direction * 2 * resizeRatio;
      this.emitResize(sizeChangeFactor);
    },
    emitResize(sizeChangeFactor) {
      this.$emit(
        'update',
        this.elements.map(element => {
          const preResizeSelection = this.interactStart.boundaries.all;
          const preResizeBoundary = this.interactStart.boundaries[element.uid];
          const preResizeElement = this.interactStart.elements[element.uid];

          const width = preResizeElement.width * Math.abs(sizeChangeFactor);
          const height = preResizeElement.height * Math.abs(sizeChangeFactor);

          return {
            uid: element.uid,
            changeset: {
              left:
                preResizeSelection.left +
                (preResizeElement.width - width) / 2 +
                (preResizeBoundary.left - preResizeSelection.left) *
                  sizeChangeFactor +
                (preResizeBoundary.width * Math.abs(sizeChangeFactor) - width) /
                  2,
              top:
                preResizeSelection.top +
                (preResizeElement.height - height) / 2 +
                (preResizeBoundary.top - preResizeSelection.top) *
                  sizeChangeFactor +
                (preResizeBoundary.height * Math.abs(sizeChangeFactor) -
                  height) /
                  2,
              width,
              height
            }
          };
        })
      );
    },
    moveElements({ x = 0, y = 0 }) {
      if (
        this.elements.filter(element => {
          return (
            element.width + element.left + x > 0 &&
            element.left + x < 100 &&
            element.height + element.top + y > 0 &&
            element.top + y < 100
          );
        }).length === 0
      ) {
        return;
      }
      this.$emit(
        'update',
        this.elements.map(element => {
          return {
            uid: element.uid,
            changeset: {
              left: element.left + x,
              top: element.top + y
            }
          };
        })
      );
    },
    showRemovalModal() {
      return new Promise((resolve, reject) => {
        this.$vfm.show({
          component: ConfirmationModal,
          bind: {
            messageText: this.$t('customization.modal.removeMessage'),
            confirmText: this.$t('customization.modal.removeConfirm'),
            declineText: this.$t('customization.modal.removeDecline')
          },
          on: {
            confirm: close => {
              close();
              resolve(true);
            },
            cancel: close => {
              close();
              reject();
            },
            closed: () => reject()
          }
        });
      });
    },
    isBelowVisibilityTreshold() {
      const dimensions = this.dimensions.all;
      const itemArea = dimensions.width * dimensions.height;

      const overlapWidth = Math.max(
        0,
        Math.min(dimensions.left + dimensions.width, 100) -
          Math.max(dimensions.left, 0)
      );
      const overlapHeight = Math.max(
        0,
        Math.min(dimensions.top + dimensions.height, 100) -
          Math.max(dimensions.top, 0)
      );

      const overlapArea = overlapWidth * overlapHeight;
      const visibleRatio = overlapArea / itemArea;

      return visibleRatio < 0.2;
    }
  }
};
</script>

<style lang="scss" module>
$size: 40px;
$space: -$size * 0.8;

.Control__pane {
  @apply absolute;
  touch-action: none;
}

.Control__interact {
  @apply absolute bg-white rounded-full flex justify-center items-center;
  width: $size;
  height: $size;
  cursor: pointer;
  display: flex;
}

.Control__resize {
  @extend .Control__interact;
  right: $space;
  bottom: $space;
}

.Control__remove {
  @extend .Control__interact;
  left: $space;
  top: $space;
}

.Control__edit {
  @extend .Control__interact;
  right: $space;
  top: $space;
}
</style>
